// frontend/src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Slider, Switch, Button, Divider, Spin } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { estimationAPI } from '../services/api';

const { Option } = Select;

const Sidebar = ({ filters, setFilters, ebkpCodes, onContinueEstimation }) => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [savedEstimations, setSavedEstimations] = useState([]);
  const [selectedEstimationId, setSelectedEstimationId] = useState(null);
  const [loadingEstimations, setLoadingEstimations] = useState(false);
  const canEstimate = hasPermission('kostenschaetzung');

  /**
   * Load saved estimations only when user is authenticated and allowed to
   */
  useEffect(() => {
    if (user && canEstimate) {
      loadSavedEstimations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, canEstimate]);

  /**
   * Load all saved estimations from backend
   */
  const loadSavedEstimations = async () => {
    try {
      setLoadingEstimations(true);
      const response = await estimationAPI.list();
      setSavedEstimations(response.data.estimations || []);
    } catch (error) {
      // Silently fail - don't show error messages
      console.debug('Could not load estimations');
    } finally {
      setLoadingEstimations(false);
    }
  };

  /**
   * Handle continue estimation button click
   */
  const handleContinueEstimation = () => {
    if (!selectedEstimationId) {
      return;
    }

    // Call parent handler
    if (onContinueEstimation) {
      onContinueEstimation(selectedEstimationId);
    }

    // Reset selection
    setSelectedEstimationId(null);
  };

  /**
   * Navigate to the KI-Suche (agent chat landing page)
   */
  const handleAgenticSearch = () => {
    navigate('/');
  };

  const handleCodesChange = (value) => {
    setFilters({ ...filters, codes: value });
  };

  const handleBuildingTypeChange = (value) => {
    setFilters({ ...filters, buildingType: value });
  };

  const handleKantonChange = (value) => {
    setFilters({ ...filters, kanton: value });
  };

  const handleSizeRangeChange = (value) => {
    setFilters({ ...filters, sizeRange: value });
  };

  const handleSizeRangeToggle = (checked) => {
    setFilters({ ...filters, sizeRangeEnabled: checked });
  };

  const handleVolRangeChange = (value) => {
    setFilters({ ...filters, volRange: value });
  };

  const handleVolRangeToggle = (checked) => {
    setFilters({ ...filters, volRangeEnabled: checked });
  };

  const handleOberirdischRangeChange = (value) => {
    setFilters({ ...filters, oberirdischRange: value });
  };

  const handleOberirdischRangeToggle = (checked) => {
    setFilters({ ...filters, oberirdischRangeEnabled: checked });
  };

  const handleYearRangeChange = (value) => {
    setFilters({ ...filters, yearRange: value });
  };

  const handleYearRangeToggle = (checked) => {
    setFilters({ ...filters, yearRangeEnabled: checked });
  };

  /**
   * Natural sort function for eBKP codes
   * Sorts A1, A1.1, A1.1.1, A1.1.2, A1.2, B1, etc.
   */
  const naturalSort = (a, b) => {
    const aCode = a.split(':')[0].trim();
    const bCode = b.split(':')[0].trim();

    return aCode.localeCompare(bCode, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  };

  // Sort eBKP codes naturally
  const sortedEbkpCodes = [...ebkpCodes].sort(naturalSort);

  // Building types
  const buildingTypes = [
    '1.1 Wohnen',
    '1.10 Sport, Freizeit und Erholung',
    '1.2 Unterricht, Bildung und Forschung',
    '1.3 Industrie, Handel und Verwaltung',
    '1.6 Fürsorge und Gesundheit',
    '1.9 Restauration und Hotellerie',
  ];

  // Swiss cantons
  const cantons = [
    'Aargau', 'Appenzell Innerrhoden', 'Appenzell Ausserrhoden',
    'Bern', 'Basel-Landschaft', 'Basel-Stadt', 'Fribourg',
    'Genève', 'Glarus', 'Graubünden', 'Jura', 'Luzern',
    'Neuchâtel', 'Nidwalden', 'Obwalden', 'St. Gallen',
    'Schaffhausen', 'Schwyz', 'Solothurn', 'Thurgau',
    'Ticino', 'Uri', 'Vaud', 'Valais', 'Zug', 'Zürich'
  ];

  return (
    <div>
      <div style={{ marginTop: '20px' }}>
        <h3>eBKP Codes</h3>
        <Select
          mode="multiple"
          placeholder="eBKP Codes auswählen"
          value={filters.codes}
          onChange={handleCodesChange}
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {sortedEbkpCodes.map((code) => (
            <Option key={code} value={code}>
              {code}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h3>Gebäudetyp</h3>
        <Select
          mode="multiple"
          placeholder="Gebäudetyp auswählen"
          value={filters.buildingType}
          onChange={handleBuildingTypeChange}
          style={{ width: '100%' }}
        >
          {buildingTypes.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h3>Kanton</h3>
        <Select
          mode="multiple"
          placeholder="Kanton auswählen"
          value={filters.kanton}
          onChange={handleKantonChange}
          style={{ width: '100%' }}
        >
          {cantons.map((kanton) => (
            <Option key={kanton} value={kanton}>
              {kanton}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Geschossfläche (m²)</h3>
          <Switch
            checked={filters.sizeRangeEnabled}
            onChange={handleSizeRangeToggle}
            size="small"
          />
        </div>
        <Slider
          range
          min={0}
          max={20000}
          step={100}
          value={filters.sizeRange}
          onChange={handleSizeRangeChange}
          disabled={!filters.sizeRangeEnabled}
        />
        <div className="wz-range-readout">
          {filters.sizeRange[0]} - {filters.sizeRange[1]} m²
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Gebäudevolumen (m³)</h3>
          <Switch
            checked={filters.volRangeEnabled}
            onChange={handleVolRangeToggle}
            size="small"
          />
        </div>
        <Slider
          range
          min={0}
          max={100000}
          step={1000}
          value={filters.volRange}
          onChange={handleVolRangeChange}
          disabled={!filters.volRangeEnabled}
        />
        <div className="wz-range-readout">
          {filters.volRange[0]} - {filters.volRange[1]} m³
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Geschossfläche oberirdisch (m²)</h3>
          <Switch
            checked={filters.oberirdischRangeEnabled}
            onChange={handleOberirdischRangeToggle}
            size="small"
          />
        </div>
        <Slider
          range
          min={0}
          max={15000}
          step={100}
          value={filters.oberirdischRange}
          onChange={handleOberirdischRangeChange}
          disabled={!filters.oberirdischRangeEnabled}
        />
        <div className="wz-range-readout">
          {filters.oberirdischRange[0]} - {filters.oberirdischRange[1]} m²
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Baudatum</h3>
          <Switch
            checked={filters.yearRangeEnabled}
            onChange={handleYearRangeToggle}
            size="small"
          />
        </div>
        <Slider
          range
          min={2000}
          max={2024}
          step={1}
          value={filters.yearRange}
          onChange={handleYearRangeChange}
          disabled={!filters.yearRangeEnabled}
        />
        <div className="wz-range-readout">
          {filters.yearRange[0]} - {filters.yearRange[1]}
        </div>
      </div>

      {/* Saved Estimations Section (nur mit Kostenschätzungs-Berechtigung) */}
      {canEstimate && (
      <>
      <Divider style={{ margin: '32px 0 24px 0' }} />

      <div style={{ marginTop: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h3 style={{ margin: 0 }}>Gespeicherte Kostenschätzungen</h3>
          <Button
            type="text"
            size="small"
            onClick={loadSavedEstimations}
            loading={loadingEstimations}
            title="Aktualisieren"
          >
            ↻
          </Button>
        </div>

        {loadingEstimations ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
          </div>
        ) : (
          <>
            <Select
              placeholder="Kostenschätzung auswählen"
              value={selectedEstimationId}
              onChange={setSelectedEstimationId}
              style={{ width: '100%', marginBottom: '12px' }}
              allowClear
              showSearch
              filterOption={(input, option) => {
                const children = option.children;
                if (Array.isArray(children)) {
                  const name = children.find(child => typeof child === 'object' && child.props?.style?.fontWeight === 500);
                  if (name && name.props && name.props.children) {
                    return name.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }
                }
                return false;
              }}
            >
              {savedEstimations.map((estimation) => (
                <Option key={estimation.id} value={estimation.id}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{estimation.name}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      {estimation.username} • {new Date(estimation.updated_at).toLocaleDateString('de-CH')}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>

            <Button
              type="primary"
              onClick={handleContinueEstimation}
              disabled={!selectedEstimationId}
              block
            >
              Kostenschätzung fortsetzen
            </Button>

            {savedEstimations.length === 0 && !loadingEstimations && (
              <div className="wz-empty-box">
                Keine gespeicherten Kostenschätzungen
              </div>
            )}
          </>
        )}
      </div>
      </>
      )}

      {/* KI-Suche Button (nur mit KI-Suche-Berechtigung) */}
      {hasPermission('ki_suche') && (
      <>
      <Divider style={{ margin: '32px 0 24px 0' }} />

      <div style={{ marginTop: '24px' }}>
        <Button
          type="primary"
          icon={<MessageOutlined />}
          onClick={handleAgenticSearch}
          block
        >
          KI-Suche
        </Button>
        <div className="wz-hint-center">
          Suche und Analyse in natürlicher Sprache
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Sidebar;